import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { filterCommands, type Command } from "@/shared/lib/commands";
import { useCommands, usePalette } from "@/shared/lib/hooks/useCommands";
import { cn } from "@/shared/lib/cn";
import { formatShortcut } from "@/shared/lib/shortcut";
import { Kbd } from "./Kbd";
import { Surface } from "./Surface";
import { Text } from "./Text";

/**
 * The ⌘K command palette. Reads whatever features have registered into the
 * command store, filters by query (`filterCommands`), and runs the selection.
 * Self-contained (overlay + portal + keyboard nav); mount once in the app shell.
 * Opening is driven by the store (`usePalette`), so any control can summon it.
 */
export function CommandPalette() {
  const { t } = useTranslation();
  const { open, setOpen } = usePalette();
  const commands = useCommands();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => filterCommands(commands, query), [commands, query]);

  // Reset when opening; keep the active item in range as the list narrows.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      inputRef.current?.focus();
    }
  }, [open]);
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  function run(command: Command | undefined): void {
    if (!command) return;
    setOpen(false);
    command.perform();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (filtered.length ? (index + 1) % filtered.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        filtered.length ? (index - 1 + filtered.length) % filtered.length : 0,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      run(filtered[activeIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // Group in display order while keeping a flat index for keyboard nav.
  const groups: { group: string; items: { command: Command; index: number }[] }[] = [];
  filtered.forEach((command, index) => {
    let bucket = groups.find((g) => g.group === command.group);
    if (!bucket) {
      bucket = { group: command.group, items: [] };
      groups.push(bucket);
    }
    bucket.items.push({ command, index });
  });

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t("commandPalette.searchPlaceholder")}
    >
      <button
        type="button"
        aria-label={t("commandPalette.close")}
        onClick={() => setOpen(false)}
        className="absolute inset-0 cursor-default bg-ink/30 backdrop-blur-[2px]"
      />

      <Surface
        tone="paper"
        radius="md"
        border="hairline"
        shadow="pill"
        className="relative flex w-full max-w-[560px] flex-col overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="shrink-0 text-ink-faint"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.2-3.2" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("commandPalette.searchPlaceholder")}
            aria-label={t("commandPalette.searchPlaceholder")}
            aria-activedescendant={
              filtered[activeIndex] ? `command-${filtered[activeIndex].id}` : undefined
            }
            className="h-12 w-full border-none bg-transparent font-sans text-[15px] text-ink outline-none placeholder:text-ink-faint"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-2" role="listbox">
          {filtered.length === 0 ? (
            <Text variant="body" className="px-3 py-6 text-center text-ink-faint">
              {t("commandPalette.noResults")}
            </Text>
          ) : (
            groups.map((bucket) => (
              <div key={bucket.group} className="mb-1 last:mb-0">
                <Text variant="label" as="div" className="px-3 pb-1 pt-2">
                  {bucket.group}
                </Text>
                {bucket.items.map(({ command, index }) => {
                  const active = index === activeIndex;
                  return (
                    <button
                      key={command.id}
                      id={`command-${command.id}`}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onPointerMove={() => setActiveIndex(index)}
                      onClick={() => run(command)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-sm px-3 py-2 text-left font-sans text-sm transition-colors duration-150 ease-house",
                        active ? "bg-line-soft text-ink" : "text-ink",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{command.title}</span>
                      {command.shortcut ? (
                        <span className="flex shrink-0 gap-1">
                          {formatShortcut(command.shortcut).map((token, i) => (
                            <Kbd key={i}>{token}</Kbd>
                          ))}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 px-4 pb-3 pt-1 text-ink-faint">
          <span className="flex items-center gap-1.5 text-[11px]">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            {t("commandPalette.navigate")}
          </span>
          <span className="flex items-center gap-1.5 text-[11px]">
            <Kbd>↵</Kbd>
            {t("commandPalette.select")}
          </span>
          <span className="flex items-center gap-1.5 text-[11px]">
            <Kbd>esc</Kbd>
            {t("commandPalette.close")}
          </span>
        </div>
      </Surface>
    </div>,
    document.body,
  );
}
